import {useState} from 'react';
import {BaseUser, generatePath, indexArrayBy, ResponsiveImage} from '@legit-health/ui';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import {Box, Button, Grid, Typography} from '@mui/material';
import {TFunction} from 'i18next';
import useSuspenseApiQuery from '@/shared/hooks/useSuspenseApiQuery';

interface TemporaryImage {
  id: number;
  url: string;
}

export default function SelectTemporaryImagesForm({
  maxImages,
  onFinish,
  patient,
  t
}: {
  maxImages: number;
  onFinish: (images: Blob[]) => void;
  patient: BaseUser;
  t: TFunction;
}) {
  const [imageIds, setImageIds] = useState<number[]>([]);
  const temporaryImagesQuery = useSuspenseApiQuery<TemporaryImage[]>({
    queryOptions: {
      queryKey: ['temporary-images', patient.id],
      staleTime: 0,
      gcTime: 0
    },
    requestArguments: {
      path: generatePath('/patients/:patientId/temporary-images', {patientId: patient.id}),
      method: 'GET'
    }
  });

  function handleImageClick(id: number) {
    const found = imageIds.includes(id);
    if (found) {
      setImageIds((i) => i.filter((i) => i !== id));
    } else {
      if (imageIds.length === maxImages) {
        return;
      }
      setImageIds((i) => [...i, id]);
    }
  }

  function handleRefresh() {
    temporaryImagesQuery.refetch();
  }

  async function handleSubmit() {
    const indexedById = indexArrayBy(temporaryImagesQuery.data.data, 'id');
    const blobs = await Promise.all(
      imageIds.map(async function (id) {
        const temporaryImage = indexedById[id] as TemporaryImage;
        const response = await fetch(temporaryImage.url);
        return response.blob();
      })
    );
    onFinish(blobs);
  }

  return (
    <Box>
      {temporaryImagesQuery.data.data.length === 0 && (
        <Typography
          sx={{
            mb: 4
          }}
        >
          {t('ui:temporaryImages.noImages')}
        </Typography>
      )}
      {temporaryImagesQuery.data.data.length > 0 && (
        <Grid
          container
          spacing={4}
          sx={{
            mb: 4
          }}
        >
          {temporaryImagesQuery.data.data.map((temporaryImage) => (
            <Grid key={temporaryImage.id} size={3}>
              <div onClick={() => handleImageClick(temporaryImage.id)}>
                <Box
                  sx={{
                    position: 'relative',
                    cursor: 'pointer'
                  }}
                >
                  {imageIds.includes(temporaryImage.id) && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        p: 1,
                        width: 40,
                        height: 40,
                        bgcolor: 'white'
                      }}
                    >
                      <CheckCircleIcon color="primary" />
                    </Box>
                  )}
                  <ResponsiveImage src={temporaryImage.url} alt="image" />
                </Box>
              </div>
            </Grid>
          ))}
        </Grid>
      )}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          pt: 2,
          borderTopWidth: 1,
          borderTopStyle: 'solid',
          borderTopColor: 'grey.400'
        }}
      >
        <Button
          onClick={handleRefresh}
          variant="text"
          color="secondary"
          disableElevation
          disabled={temporaryImagesQuery.isFetching}
          sx={{
            mr: 2
          }}
          startIcon={<RefreshIcon />}
        >
          {temporaryImagesQuery.isFetching
            ? t('ui:temporaryImages.refreshingImages')
            : t('ui:temporaryImages.refreshImages')}
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="secondary" disableElevation>
          {t('shared/common:select')}
        </Button>
      </Box>
    </Box>
  );
}
